import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { useCallback, useEffect, useState } from 'react'
import { Plus, RefreshCw, ChevronDown, Database } from 'lucide-react'

const API = '/api/deploy/database-clusters'
const messages = {
  CONNECTION_NOT_CONFIGURED: '모니터링 연결 설정이 필요합니다.',
  CITUS_NOT_INSTALLED: '대상 PostgreSQL에 Citus 확장이 설치되어 있지 않습니다.',
  CLUSTER_AUTH_FAILED: '모니터링 계정을 확인해 주세요.',
  CLUSTER_PERMISSION_REQUIRED: '모니터링 계정에 메타데이터 조회 권한이 필요합니다.',
  CLUSTER_UNAVAILABLE: '클러스터에 연결할 수 없습니다.',
  DATABASE_CATALOG_UNAVAILABLE: '클러스터 목록을 불러올 수 없습니다.',
  INVALID_DATABASE_CLUSTER: '이름과 PostgreSQL 주소를 확인해 주세요.',
  DATABASE_CLUSTER_EXISTS: '이미 등록된 클러스터 주소입니다.',
  DATABASE_CHECK_BUSY: '상태를 확인하고 있습니다. 잠시 후 다시 시도해 주세요.',
  LOGIN_REQUIRED: '로그인이 필요합니다.', INVALID_SESSION: '로그인이 만료됐습니다.',
  ADMIN_REQUIRED: '스카이넷 관리자 권한이 필요합니다.',
  NODE_HEALTH_UNAVAILABLE: '노드 간 연결 상태를 조회하지 못했습니다.',
  NODE_HEALTH_LIMIT: '32개를 초과하는 활성 노드는 연결 전수 검사 대상에서 제외됩니다.',
  SHARD_METRICS_UNAVAILABLE: '샤드 배치 용량을 조회하지 못했습니다.',
}
const stateText = { healthy: '정상', degraded: '확인 필요', unknown: '미확인',
  live: '연결됨', unavailable: '연결 실패', inactive: '배치 비활성', stale: '재확인 필요' }
function headers() {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token')
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}
async function request(path = '', options = {}) {
  const response = await fetch(API + path, { credentials: 'include', headers: headers(), ...options })
  const body = await response.json()
  if (!response.ok) throw Error(messages[body.code] || '요청을 처리하지 못했습니다.')
  return body
}
function bytes(value) {
  if (value === null || value === undefined) return '미확인'
  if (value < 1024) return `${value} B`
  const units = ['KiB', 'MiB', 'GiB', 'TiB']; let n = value / 1024; let i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(1)} ${units[i]}`
}
function time(value) { return value ? new Date(value).toLocaleString('ko-KR') : '조회 전' }
function stale(snapshot, now) {
  const date = Date.parse(snapshot?.observedAt)
  return !Number.isFinite(date) || now - date > 120000 || date > now + 30000
}

export default function DistributedDatabase() {
  const [clusters, setClusters] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const [selected, setSelected] = useState(null)
  const [busy, setBusy] = useState(null)
  const [form, setForm] = useState({ name: '', endpoint: '' })
  const [now, setNow] = useState(Date.now())
  const load = useCallback(async () => {
    setLoading(true); setError('')
    try { setClusters((await request()).clusters) }
    catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [])
  useEffect(() => { load(); const timer = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(timer) }, [load])
  useEffect(() => {
    let cancelled = false
    const timer = setInterval(async () => {
      if (document.visibilityState === 'hidden' || busy) return
      try {
        const data = await request()
        if (!cancelled) { setClusters(data.clusters); setNow(Date.now()) }
      } catch (e) { if (!cancelled) setError(e.message) }
    }, 30000)
    return () => { cancelled = true; clearInterval(timer) }
  }, [busy])
  async function create(event) {
    event.preventDefault(); setBusy('new'); setError('')
    try {
      const { cluster } = await request('', { method: 'POST', body: JSON.stringify({ ...form, engine: 'citus' }) })
      setClusters(items => [...items, cluster]); setSelected(cluster.id); setCreating(false); setForm({ name: '', endpoint: '' })
    } catch (e) { setError(e.message) } finally { setBusy(null) }
  }
  async function refresh(id) {
    setBusy(id); setError('')
    try {
      const { cluster } = await request(`/${id}/refresh`, { method: 'POST' })
      setClusters(items => items.map(item => item.id === id ? cluster : item)); setNow(Date.now())
    } catch (e) { setError(e.message) } finally { setBusy(null) }
  }
  async function remove(cluster) {
    if (!window.confirm(`${cluster.name}의 모니터링 연결을 삭제할까요? 데이터베이스와 노드는 유지됩니다.`)) return
    setBusy(cluster.id); setError('')
    try { await request(`/${cluster.id}`, { method: 'DELETE' }); setClusters(items => items.filter(item => item.id !== cluster.id)); setSelected(null) }
    catch (e) { setError(e.message) } finally { setBusy(null) }
  }
  return <div className="distributed-db">
    <div className="ddb-toolbar"><div><h2>Citus 클러스터</h2><p>PostgreSQL의 분산 노드와 샤드 배치를 확인합니다.</p></div>
      <button className="ddb-primary" onClick={() => setCreating(!creating)}><Plus size={16} />클러스터 연결</button></div>
    {error && <p role="alert" className="ddb-error">{error}</p>}
    {creating && <form className="ddb-form" onSubmit={create}>
      <label>이름<input required maxLength={80} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="예: 서비스 운영 DB" /></label>
      <label>코디네이터 주소<input required value={form.endpoint} onChange={e => setForm({ ...form, endpoint: e.target.value })} placeholder="postgresql://db.example.com:5432/app" /></label>
      <p>인증 정보는 서버의 모니터링 연결 설정을 사용합니다.</p>
      <div><button className="ddb-primary" disabled={busy === 'new'}>{busy === 'new' ? '저장 중…' : '연결 등록'}</button><button type="button" onClick={() => setCreating(false)}>취소</button></div>
    </form>}
    {loading ? <p className="ddb-empty" role="status">클러스터를 불러오는 중…</p>
      : clusters.length === 0 && !error ? <div className="ddb-empty"><Database size={28} /><h3>연결된 분산 DB가 없습니다</h3><p>Citus 클러스터를 연결하면 노드와 샤드 현황이 표시됩니다.</p></div>
        : <div className="ddb-list">{clusters.map(cluster => {
          const snapshot = cluster.snapshot
          const isStale = snapshot && stale(snapshot, now)
          const state = isStale ? 'stale' : snapshot?.state || 'unknown'
          const expanded = selected === cluster.id
          return <article className="ddb-cluster" key={cluster.id}>
            <button className="ddb-cluster-heading" aria-expanded={expanded} aria-controls={`cluster-${cluster.id}`} onClick={() => setSelected(expanded ? null : cluster.id)}>
              <span><strong>{cluster.name}</strong><small>Citus · PostgreSQL{snapshot ? ` · 코디네이터 ${snapshot.nodes.filter(n => n.role === 'coordinator').length} · 워커 ${snapshot.nodes.filter(n => n.role === 'worker').length}` : ''}</small></span>
              <span className={`ddb-state ${state}`}>{!snapshot && !cluster.lastError ? '미연결' : cluster.lastError ? '연결 확인 필요' : stateText[state]}</span>
              <ChevronDown size={18} className={expanded ? 'expanded' : ''} />
            </button>
            {expanded && <div className="ddb-detail" id={`cluster-${cluster.id}`}>
              <div className="ddb-detail-header"><code>{cluster.endpoint}</code><button disabled={busy === cluster.id} onClick={() => refresh(cluster.id)}><RefreshCw size={15} />{busy === cluster.id ? '조회 중…' : '상태 조회'}</button></div>
              {cluster.lastError && <p className="ddb-notice" role="status">{messages[cluster.lastError] || '클러스터 상태를 조회하지 못했습니다.'}</p>}
              {!snapshot && <p className="ddb-notice">{cluster.lastError ? '확인된 노드 정보가 없습니다.' : '아직 상태를 조회하지 않았습니다.'}</p>}
              {snapshot && <>
                <div className="ddb-summary"><span>등록 노드 <strong>{snapshot.nodeCount}</strong></span><span>연결 확인 <strong>{isStale ? '재조회 필요' : `${snapshot.liveCount} / ${snapshot.nodeCount}`}</strong></span><span>Citus <strong>{snapshot.version}</strong></span></div>
                {isStale && <p className="ddb-notice">2분 이상 지난 조회 결과입니다. 현재 상태를 다시 조회해 주세요.</p>}
                {snapshot.warnings?.map(warning => <p className="ddb-notice" key={warning}>{messages[warning] || '일부 항목을 확인하지 못했습니다.'}</p>)}
                <div className="ddb-nodes" aria-label={`${cluster.name} 노드 목록`}>
                  <div className="ddb-node-head"><span>노드</span><span>상태</span><span>샤드 배치</span><span>샤드 용량</span></div>
                  {snapshot.nodes.map(node => <div className="ddb-node" key={node.id}>
                    <div><strong>{node.role === 'coordinator' ? '코디네이터' : '워커'} {node.id}</strong><code>{node.address}</code><small>{node.replicationRole === 'secondary' ? '복제본' : '주 노드'} · 그룹 {node.groupId}</small></div>
                    <span data-label="상태" className={`ddb-state ${isStale ? 'stale' : node.state}`}>{stateText[isStale ? 'stale' : node.state]}</span>
                    <span data-label="샤드 배치">{node.shardCount ?? '미확인'}</span>
                    <span data-label="샤드 용량">{bytes(node.shardBytes)}</span>
                  </div>)}
                </div>
                <p className="ddb-footnote">샤드 용량은 배치된 데이터의 크기입니다. 노드 디스크 전체 용량과 복제 지연은 별도 측정이 필요합니다.</p>
              </>}
              <div className="ddb-detail-footer"><small>최근 조회: {time(cluster.checkedAt)}</small><button className="ddb-text-button" disabled={busy === cluster.id} onClick={() => remove(cluster)}>연결 삭제</button></div>
              {!snapshot && <details className="ddb-setup"><summary>연결 설정 정보</summary><p>클러스터 ID: <code>{cluster.id}</code></p><p>서버에 이 ID의 읽기 전용 모니터링 계정과 TLS 인증서를 등록한 뒤 상태를 조회하세요.</p></details>}
            </div>}
          </article>
        })}</div>}
    <details className="ddb-guide"><summary>PostgreSQL에서 단계적으로 확장하기</summary><p>일반 PostgreSQL → Citus 설치·호환성 확인 → 서비스별 분산 키와 샤드 설계 → 워커 추가·데이터 재배치 순서로 진행합니다. 노드 등록과 데이터 분산, 복제·장애 복구 설정은 각각 확인해야 합니다.</p></details>
  </div>
}
